import {
  Box,
  CircularProgress,
  SxProps,
  Tooltip,
  Typography,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Paginated } from "../../common/types/Paginated";
import {
  ExclusivityFormatLabels,
  formatExclusivityEntry,
} from "../talentProfile/exclusivity/exclusivityHelpers";
import { TalentExclusivity } from "./talentAPI";
import { fetchExclusivitiesByTalentId } from "./talentsSlice";

interface Props {
  talent: { id: string };
  labelSx?: SxProps;
  labelOnly?: boolean;
}

const PoolProLabel = "PoolPro";

export const PoolProPopper = ({
  talent,
  labelSx,
  labelOnly = false,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const workTitleTranslations = useAppSelector(
    (state) => state.localized.workTitle,
  );
  const [exclusivities, setExclusivities] = useState<
    TalentExclusivity[] | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpen = useCallback(() => {
    if (exclusivities !== null || isLoading || labelOnly) return;
    setIsLoading(true);
    dispatch(
      fetchExclusivitiesByTalentId({ talentId: talent.id, pageSize: 500 }),
    )
      .then((action) => {
        if (isFulfilled(action)) {
          setExclusivities(
            (action.payload as Paginated<TalentExclusivity[]>).data ?? [],
          );
        }
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, exclusivities, isLoading, talent.id, labelOnly]);

  const labels: ExclusivityFormatLabels = {
    allWorkTitles: t("talentProfile:allWorkTitles"),
    allOrganizations: t("talentProfile:allOrganizations"),
    allWorkLocations: t("talentProfile:allWorkLocations"),
  };

  const tooltipContent = isLoading ? (
    <Box
      minWidth="74px"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <CircularProgress size={20} sx={{ color: "grey.400" }} />
    </Box>
  ) : (
    <Box>
      {exclusivities?.map((e, index) => (
        <Box key={e.id} mt={index > 0 ? 1 : 0} maxWidth="400px">
          {formatExclusivityEntry(e, workTitleTranslations, labels)}
        </Box>
      ))}
    </Box>
  );

  return (
    <Tooltip
      title={labelOnly ? null : tooltipContent}
      onOpen={handleOpen}
      enterDelay={200}
      componentsProps={{ tooltip: { sx: { maxWidth: "none" } } }}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            color: "primary.main",
            fontSize: "0.75rem",
            fontWeight: 900,
            cursor: "default",
            ...labelSx,
          }}
        >
          {PoolProLabel}
        </Typography>
      </Box>
    </Tooltip>
  );
};
